import { useState, useRef, useEffect } from "react"

/**
 * Returns a tooltip that displays a message when the user hovers over a component
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - The component to display the tooltip on
 * @param {React.ReactNode} props.content - The message to display in the tooltip

 * @param {boolean} props.editMode - Whether the tooltip is in edit mode
 * @returns {React.ReactNode}
 */
const Tooltip = ({ children, content, editMode }) => {
	const [hovered, setHovered] = useState(false)
	const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 })

	const tooltipRef = useRef(null)

	const handleMouseEnter = e => {
		setHovered(true)

		const { left, top } = e.target.getBoundingClientRect()
		setTooltipPosition({
			top,
			left
		})
	}

	useEffect(() => {
		if (hovered && tooltipRef.current) {
			const tooltipWidth = tooltipRef.current.offsetWidth
			const tooltipHeight = tooltipRef.current.offsetHeight
			const screenWidth = window.innerWidth

			setTooltipPosition(prev => {
				let newLeft = prev.left
				let newTop = prev.top - tooltipHeight - 8

				if (newLeft + tooltipWidth > screenWidth) {
					newLeft = screenWidth - tooltipWidth - 8
				}

				if (newLeft < 8) {
					newLeft = 8
				}

				return {
					top: newTop,
					left: newLeft
				}
			})
		}
	}, [hovered])

	const handleMouseLeave = () => {
		setHovered(false)
	}

	return (
		<>
			<div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
				{children}
			</div>
			{hovered && !editMode && (
				<div
					ref={tooltipRef}
					className="fixed z-50 pointer-events-none"
					style={{
						top: `${tooltipPosition.top}px`,
						left: `${tooltipPosition.left}px`,
						transform: "translateX(0)"
					}}
				>
					{content}
				</div>
			)}
		</>
	)
}

export default Tooltip
